import { PRICING_PLANS } from '../config/pricing-data';

function normalizeSlug(slug?: string | null): string {
  return slug?.trim().toLowerCase() ?? '';
}

const METRIC_FOR_FEATURE: Record<string, string> = {
  maxProjects: 'PROJECTS',
  maxWorkspaces: 'WORKSPACES',
  storageLimitMB: 'STORAGE_MB',
  dailyTestEmails: 'EMAIL_SENDS',
  monthlyExports: 'MONTHLY_EXPORTS',
  monthlyApiLimit: 'API_CALLS',
};

export function reconcilePlan(plan: any): any {
  if (!plan) return plan;
  const slug = normalizeSlug(plan.slug);
  const staticPlan = PRICING_PLANS.find((p) => normalizeSlug(p.slug) === slug);

  // Map of usage limits from plan.limits (if present)
  const limitsMap = new Map<string, number | null>();
  if (Array.isArray(plan.limits)) {
    for (const l of plan.limits) {
      if (l && l.metric) {
        limitsMap.set(l.metric, l.limit);
      }
    }
  }

  const staticFeatureMap = new Map<string, any>();
  if (staticPlan?.features) {
    for (const f of staticPlan.features) {
      staticFeatureMap.set(f.key, f);
    }
  }

  const existingFeatureKeys = new Set<string>();
  const reconciledFeatures: any[] = [];

  for (const f of plan.features || []) {
    existingFeatureKeys.add(f.key);
    const staticF = staticFeatureMap.get(f.key);
    let resolvedValue = f.value;
    let resolvedIncluded = f.included;

    // 1. Metric limit from usage limits
    const metricName = METRIC_FOR_FEATURE[f.key];
    if (metricName && limitsMap.has(metricName)) {
      const limitVal = limitsMap.get(metricName);
      if (limitVal === null) {
        resolvedValue = true; // Unlimited
        resolvedIncluded = true;
      } else if (typeof limitVal === 'number') {
        resolvedValue = limitVal;
        resolvedIncluded = limitVal > 0;
      }
    } else if (
      (typeof f.value === 'boolean' || f.value === null || f.value === undefined) &&
      staticF &&
      typeof staticF.value === 'number'
    ) {
      resolvedValue = staticF.value;
      resolvedIncluded = staticF.included;
    } else if (staticF && typeof staticF.value === 'boolean') {
      if (staticF.value === false) {
        resolvedValue = false;
        resolvedIncluded = false;
      } else {
        resolvedValue = staticF.value;
        resolvedIncluded = staticF.included;
      }
    } else if (staticF && staticF.value !== undefined) {
      if (typeof f.value === 'boolean' && typeof staticF.value !== 'boolean') {
        resolvedValue = staticF.value;
        resolvedIncluded = staticF.included;
      }
    }

    // High resource restrictions on Free and Starter
    if (slug === 'free' || slug === 'starter') {
      if (f.key === 'byok' || f.key === 'apiAccess') {
        resolvedValue = false;
        resolvedIncluded = false;
      }
      if (f.key === 'monthlyApiLimit' || f.key === 'rateLimitPerMinute') {
        resolvedValue = 0;
        resolvedIncluded = false;
      }
    }

    // Explicit plan limit guarantees
    if (slug === 'free') {
      if (f.key === 'maxProjects' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 3;
      if (f.key === 'maxWorkspaces' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 1;
      if (f.key === 'storageLimitMB' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 50;
      if (f.key === 'dailyTestEmails' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 3;
      if (f.key === 'dailyDownloads' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 5;
      if (f.key === 'monthlyExports' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 5;
      if (f.key === 'versionHistoryDays' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 1;
      if (f.key === 'maxVersions' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 3;
      if (f.key === 'aiCredits' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 10;
    } else if (slug === 'starter') {
      if (f.key === 'maxProjects' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 15;
      if (f.key === 'maxWorkspaces' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 5;
      if (f.key === 'storageLimitMB' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 200;
      if (f.key === 'dailyTestEmails' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 10;
      if (f.key === 'versionHistoryDays' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 7;
      if (f.key === 'maxVersions' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 10;
      if (f.key === 'dailyDownloads') resolvedValue = true;
      if (f.key === 'monthlyExports') resolvedValue = true;
      if (f.key === 'aiCredits' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 100;
    } else if (slug === 'pro') {
      if (f.key === 'maxProjects' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 100;
      if (f.key === 'maxWorkspaces' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 10;
      if (f.key === 'storageLimitMB' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 2000;
      if (f.key === 'dailyTestEmails' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 50;
      if (f.key === 'versionHistoryDays' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 30;
      if (f.key === 'maxVersions' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 50;
      if (f.key === 'monthlyApiLimit' && (resolvedValue === true || typeof resolvedValue !== 'number')) {
        resolvedValue = 50000;
        resolvedIncluded = true;
      }
      if (f.key === 'rateLimitPerMinute' && (resolvedValue === true || typeof resolvedValue !== 'number')) {
        resolvedValue = 60;
        resolvedIncluded = true;
      }
      if (f.key === 'dailyDownloads') resolvedValue = true;
      if (f.key === 'monthlyExports') resolvedValue = true;
      if (f.key === 'aiCredits' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 500;
    } else if (slug === 'enterprise') {
      if (f.key === 'maxProjects') resolvedValue = true;
      if (f.key === 'maxWorkspaces') resolvedValue = true;
      if (f.key === 'storageLimitMB' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 25000;
      if (f.key === 'dailyTestEmails' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 500;
      if (f.key === 'dailyDownloads') resolvedValue = true;
      if (f.key === 'monthlyExports') resolvedValue = true;
      if (f.key === 'versionHistoryDays') resolvedValue = true;
      if (f.key === 'maxVersions') resolvedValue = true;
      if (f.key === 'monthlyApiLimit' && (resolvedValue === true || typeof resolvedValue !== 'number')) {
        resolvedValue = 500000;
        resolvedIncluded = true;
      }
      if (f.key === 'rateLimitPerMinute' && (resolvedValue === true || typeof resolvedValue !== 'number')) {
        resolvedValue = 300;
        resolvedIncluded = true;
      }
      if (f.key === 'aiCredits' && (resolvedValue === true || typeof resolvedValue !== 'number')) resolvedValue = 2500;
    }

    reconciledFeatures.push({
      ...f,
      value: resolvedValue,
      included: resolvedIncluded,
    });
  }

  if (staticPlan?.features) {
    for (const sf of staticPlan.features) {
      if (!existingFeatureKeys.has(sf.key)) {
        reconciledFeatures.push({ ...sf });
      }
    }
  }

  return {
    ...plan,
    features: reconciledFeatures,
  };
}

export function reconcilePlans(plans: any[]): any[] {
  if (!Array.isArray(plans)) return [];
  return plans.map(reconcilePlan);
}
